import React, { useEffect, useMemo, useState } from "react";
import { Box, ThemeProvider, Typography, styled, TextField } from "@mui/material";
import Modal from "../Modal";
import Button from "../Button";
import customTheme from "../../../thems/filterModalThems";
import ChipSelect from "../FilterModal/ChipSelect";
import apiClient from "lib/api-client";
import { useLocation, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useBinnysCompanyClient, useLiveStreamClient } from "./StreamingModalApi";
import { useQueryClient } from "@tanstack/react-query";
import { useGlobalContext } from "context/GlobalContext";
import { findSpecificMenu, findSpecificSubMenu, isAllowed } from "utils/checkUserRigths";
import { usePermissions } from "context/PermissionsContext";

const CustomButton = styled(Button)(({ theme, isReady }) => ({
  width: "100%",
  fontFamily: "Poppins",
  fontWeight: 500,
  fontSize: "14px",
  lineHeight: "22px",
  height: "50px",
  borderRadius: "5px",
  color: "#000000",
  textTransform: "none",
  backgroundColor: isReady ? "#50C878" : "#424242",
  border: `1px solid ${isReady ? "#50C878" : "#424242"}`,
  boxShadow: "none",
  "&:hover": {
    backgroundColor: isReady ? "#50C878" : "#424242",
    boxShadow: "none",
  },
  "&:active": {
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    backgroundColor: "#424242",
    color: "#000000",
    border: "1px solid #424242",
  },
}));

function StreamingModal({ open, setOpen, title, btnTitle, completeObject, setCompleteObject, removeModal, setRemoveModal }) {
  const [selectedClients, setSelectedClients] = useState([]);
  const location = useLocation();
  const params = useParams();
  const catalogId = location?.pathname?.includes("/Catalog") ? params?.id || "" : "";
  const [days, setDays] = useState(7);
  const queryClient = useQueryClient();
  const [isClientDropdownOpen, setIsClientDropdownOpen] = useState(false);
  const [clientOptions, setClientOptions] = useState([]);
  const [clientRawList, setClientRawList] = useState([]);
  const { setIsLiveStreaming } = useGlobalContext();
  const permissions = usePermissions();
  const hasViewStreamingEditAccess = isAllowed(findSpecificSubMenu(findSpecificMenu("Vision Web", permissions), "Live Streaming"), "edit");

  const hasViewStreamingCancelAccess = isAllowed(findSpecificSubMenu(findSpecificMenu("Vision Web", permissions), "Live Streaming"), "cancel");

  const { data: clientsData, isLoading: companyClientLoading } = useBinnysCompanyClient({ enabled: completeObject?.open });
  const { data: liveStreamData, isLoading: liveClientLoading } = useLiveStreamClient({ catalogId });

  useEffect(() => {
    setClientRawList(clientsData?.data || []);
    const formattedClients = clientsData?.data?.map((client) => client?.clientName) || [];
    setClientOptions(formattedClients || []);
  }, [clientsData]);

  useEffect(() => {
    const formattedClientsName = liveStreamData?.data?.map((client) => client?.name) || [];
    setSelectedClients(formattedClientsName || []);
    setDays(liveStreamData?.data?.[0]?.durationDays || 7);
    if (formattedClientsName?.length > 0) {
      setIsLiveStreaming(true);
    } else {
      setIsLiveStreaming(false);
    }
  }, [liveStreamData, completeObject?.open]);

  const currentLength = useMemo(() => selectedClients?.reduce((t, i) => t + String(i?.length || "").replace("...", "").length, 0), [selectedClients]);

  const handleClose = () => {
    if (completeObject) {
      setCompleteObject((prev) => ({
        ...prev,
        open: false,
      }));

      setTimeout(() => {
        setCompleteObject((prev) => ({
          ...prev,
          isEditLiveStreamModal: false,
          removeModalMode: "",
        }));
      }, 500);
    } else {
      setOpen(false);
    }

    setRemoveModal(false);
    setSelectedClients([]);
    setDays(7);
  };

  const handleDeleteClient = (clientToDelete) => {
    setSelectedClients((prev) => prev.filter((client) => client !== clientToDelete));
  };

  const handleClientChange = (e) => {
    const { value } = e.target;
    setSelectedClients(typeof value === "string" ? value.split(",") : value);
  };

  const isReady = useMemo(() => selectedClients && selectedClients?.length > 0, [selectedClients]);

  const handleEditLiveStream = () => {
    setCompleteObject({
      ...completeObject,
      title: "Edit live Stream",
      btnTitle: "Update",
      open: true,
      isEditLiveStreamModal: false,
    });
  };

  const handleCancelLiveStream = () => {
    setRemoveModal(true);
  };

  const handleCreateStream = async () => {
    try {
      let myBinnysCompanyIds = selectedClients.map((name) => {
        const found = clientRawList.find((c) => c.clientName === name);
        return found?._id;
      });
      const payload = {
        catalogId: catalogId,
        userType: "MyBinnysClientCompany",
        userIds: myBinnysCompanyIds,
        durationDays: days,
      };
      const response = await apiClient.post("/api/v1/catalog/liveStream/assign", payload);
      handleClose();
      toast.success(response?.data?.message, {
        autoClose: 2000,
        hideProgressBar: false,
      });
      queryClient.invalidateQueries(["live-stream-client"]);
      queryClient.invalidateQueries(["catalogData"]);
      setIsLiveStreaming(true);
    } catch (error) {
      console.log("error creating stream");
      toast.error(error?.response?.data?.message, {
        autoClose: 2000,
        hideProgressBar: false,
      });
    }
  };

  const handleEditStream = async () => {
    try {
      let myBinnysCompanyIds = selectedClients.map((name) => {
        const found = clientRawList.find((c) => c.clientName === name);
        return found?._id;
      });
      const payload = {
        catalogId: catalogId,
        userType: "MyBinnysClientCompany",
        userIds: myBinnysCompanyIds,
        durationDays: days,
      };
      const response = await apiClient.post("/api/v1/catalog/liveStream/edit", payload);
      handleClose();
      toast.success(response?.data?.message, {
        autoClose: 2000,
        hideProgressBar: false,
      });
      queryClient.invalidateQueries(["live-stream-client"]);
    } catch (error) {
      console.log("error creating stream");
      toast.error(error?.response?.data?.message, {
        autoClose: 2000,
        hideProgressBar: false,
      });
    }
  };

  return (
    <ThemeProvider theme={customTheme}>
      <Modal
        open={open || (completeObject?.open && !removeModal)}
        handleClose={handleClose}
        style={{
          width: "357px",
        }}
      >
        {completeObject?.isEditLiveStreamModal ? (
          <Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "115px",
                gap: "12px",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Poppins",
                  fontWeight: 600,
                  fontSize: "22px",
                  color: "#000",
                  marginBottom: "10px",
                }}
              >
                Manage live stream
              </Typography>
              {hasViewStreamingEditAccess && (
                <Typography
                  onClick={() => handleEditLiveStream()}
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: "16px",
                    cursor: "pointer",
                    color: "#333",
                  }}
                >
                  Edit Live Stream
                </Typography>
              )}

              {/* Cancel Option */}
              {hasViewStreamingCancelAccess && (
                <Typography
                  onClick={() => handleCancelLiveStream()}
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: "16px",
                    cursor: "pointer",
                    color: "#333",
                  }}
                >
                  Cancel Live Stream
                </Typography>
              )}
            </Box>
          </Box>
        ) : (
          <Box>
            <Box sx={{ display: "flex", flexDirection: "column", height: isClientDropdownOpen ? "410px" : currentLength > 3 ? "320px" : "280px" }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ marginBottom: "30px" }}>
                <Typography
                  sx={{
                    fontFamily: "Poppins",
                    fontWeight: 600,
                    fontSize: "24px",
                    color: "#000000",
                  }}
                >
                  {title || completeObject?.title}
                </Typography>
              </Box>

              <Box sx={{ marginBottom: "30px" }}>
                <Typography
                  sx={{
                    fontFamily: "Poppins",
                    fontWeight: 500,
                    fontSize: "20px",
                    marginBottom: "8px",
                    color: "#000000",
                  }}
                >
                  Client
                </Typography>
                <ChipSelect
                  value={selectedClients}
                  onChange={handleClientChange}
                  onDelete={handleDeleteClient}
                  options={clientOptions}
                  placeholder="Select client"
                  width="100%"
                  multiple={true}
                  modal="live stream"
                  setParentOpen={setIsClientDropdownOpen}
                  chipSx={{
                    fontFamily: "Karla",
                    fontWeight: 700,
                    fontSize: "14px",
                    fontStyle: "bold",
                    lineHeight: "100%",
                    letterSpacing: "0px",
                    minHeight: "20px",
                    maxHeight: "100px",
                  }}
                  menuStyles={{
                    fontFamily: "Karla",
                    fontWeight: 700,
                    width: "345px",
                    fontSize: "14px",
                    lineHeight: "100%",
                    letterSpacing: "0px",
                    maxHeight: "140px",
                    overflowY: "auto",
                    width: "fit-content",
                    // maxHeight: "30px",
                  }}
                />
              </Box>

              <Box sx={{ width: "355px", position: "absolute", bottom: 110 }}>
                <Box>
                  <Typography
                    sx={{
                      fontFamily: "Poppins",
                      fontWeight: 500,
                      fontSize: "20px",
                      marginBottom: "8px",
                      color: "#000000",
                    }}
                  >
                    Days
                  </Typography>
                  <TextField
                    type="number"
                    value={days}
                    onChange={(e) => setDays(e.target.value)}
                    fullWidth
                    onBlur={() => {
                      if (days === "" || Number(days) < 7) {
                        setDays(7);
                      } else {
                        setDays(Number(days));
                      }
                    }}
                    inputProps={{ min: 7 }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "44px",
                        fontFamily: "Karla",
                        fontSize: "18px",
                        borderRadius: "5px",
                        "& fieldset": {
                          borderColor: "#000000",
                        },
                        "&:hover fieldset": {
                          borderColor: "#000000",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#000000",
                          borderWidth: "1px",
                        },
                      },
                    }}
                  />
                  <Typography
                    sx={{
                      color: "#8B8B8B",
                      fontSize: "14px",
                      fontFamily: "Karla",
                      marginTop: "6px",
                    }}
                  >
                    The default duration is 7 days
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                marginTop: "10px",
                width: "100%",
              }}
            >
              <CustomButton
                sx={{ height: "63px", width: "355px", fontFamily: "Poppins", fontWeight: 500, fontSize: "18px" }}
                disabled={!isReady}
                isReady={isReady}
                variant="contained"
                onClick={() => (completeObject?.btnTitle === "Create" ? handleCreateStream() : handleEditStream())}
              >
                {btnTitle || completeObject?.btnTitle}
              </CustomButton>
            </Box>
          </Box>
        )}
      </Modal>
    </ThemeProvider>
  );
}

export default StreamingModal;
